<template>
  <div class="data-table">
    <vue-filter-card 
      v-if="showFilter"
      :title="filterTitle"
      :filter="internalFilter"
      :fields="filterFields"
      @filter="onFilter"
      @reset="onReset"/>
    <div class="table-controls mb-3">
      <div class="search-filter">
        <input type="text" class="form-control" v-model="internalSearchTerm" placeholder="Search..."
          @input="onSearchChange" />
      </div>

      <div class="d-flex">
        <div class="rows-per-page mx-2">
          <select id="rowsPerPage" class="form-select" v-model="internalItemsPerPage" @change="onItemsPerPageChange">
            <option v-for="option in [10, 25, 50, 100, 500]" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>

        <div class="pagination">
          <button class="btn btn-outline-secondary" :disabled="internalCurrentPage === 1" @click="prevPage">
            <i class="bi bi-rewind fs-4"></i>
          </button>
          <span class="mx-2">Page {{ internalCurrentPage }} of {{ totalPages }}</span>
          <button class="btn btn-outline-secondary" :disabled="internalCurrentPage === totalPages" @click="nextPage">
            <i class="bi bi-fast-forward fs-4"></i>
          </button>
        </div>

        <div class="">
          <div class="d-flex flex-wrap gap-2">
            <button
              v-for="(btn, index) in tableActions"
              :key="index"
              class="btn d-flex align-items-center gap-2 ms-2"
              :class="btn.class"
              @click="btn.action()"
            >
              <i v-if="btn.icon" :class="btn.icon"></i>
              {{ btn.title }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <table class="table table-striped table-bordered">
      <thead>
        <tr>
          <th v-if="showRowIndex" class="text-center text-muted" style="width: 3rem">N°</th>
          <th class="clickable vertical-align-top" v-for="(header, index) in headers" :key="index"
            @click="sortBy(header.field)">
            <div class="d-flex justify-content-between align-items-start">
              <span class="w-auto ">{{ header.label }}</span>
              <span v-if="sortField === header.field" :class="['sort-icon', sortDirection]">
                <i v-if="sortDirection === 'asc'" class="bi bi-caret-up fs-4"></i>
                <i v-else class="bi bi-caret-down-fill fs-4"></i>
              </span>
            </div>
          </th>

           <!-- Fixed columns -->
      <th class="clickable vertical-align-top">Créé le</th>
      <th class="clickable vertical-align-top">Créé par</th>

          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-if="paginatedData.length">
          <tr v-for="(row, index) in paginatedData" :key="index"
            :class="{ 'table-primary': selectedRows.includes(row) }">
            <td v-if="showRowIndex" class="text-center text-muted">{{ rowDisplayIndex(index) }}</td>
            <td v-for="(column, columnIndex) in headers" :key="columnIndex" :class="column.class">
              <slot :name="`cell-${column.field}`" :row="row" :column="column">
                {{ formatCellValue(row[column.field], column) }}
              </slot>
            </td>

            <!-- Fixed created_at -->
        <td class="text-center text-muted">
          <slot name="cell-created_at" :row="row">
            {{ row.created_at ? new Date(row.created_at).toLocaleDateString('fr-FR') : '—' }}
          </slot>
        </td>

        <!-- Fixed userFullName -->
        <td class="text-center fw-semibold text-dark">
          <slot name="cell-userFullName" :row="row">
            {{ row.userFullName || '—' }}
          </slot>
        </td>

            <td>
              <div class="btn-group">
                <button v-for="(button, buttonIndex) in filteredButtons(row)" :key="buttonIndex" class="btn"
                  :class="button.class" @click="button.action(row)">
                  <i v-if="button.icon" :class="button.icon"></i>
                  {{ button.label }}
                </button>
              </div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td :colspan="emptyRowColspan" class="text-center">No Data</td>
          </tr>
        </template>
      </tbody>

      <tfoot>
        <tr>
          <td v-if="showRowIndex"></td>
          <td v-for="(header, index) in headers" :key="index" :class="getFooterClass(header.field)">
            <template v-if="isSummableColumn(header.field)">
              <slot :name="`footer-${header.field}`" :column="header" :sum="getFormattedSum(header.field)">
                <span class="fw-bold">{{ getFormattedSum(header.field) }}</span>
              </slot>
            </template>
          </td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>




<script>
export default {
  name: "DataTable",
  props: {
    headers: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    buttons: {
      type: Array,
      default: () => []
    },
    summableColumns: {
      type: Array,
      default: () => []
    },
    // New props for external control
    page: {
      type: Number,
      default: null
    },
    searchQuery: {
      type: String,
      default: null
    },
    // Whether to sync URL with table state
    syncWithUrl: {
      type: Boolean,
      default: true
    },
    tableActions: {
      type: Array,
      default: () => [],
    },
    // 🔽 Filter Props
    showFilter: { type: Boolean, default: true },
    filterFields: { type: Array, default: () => [] },
    filterTitle: { type: String, default: "Filtrer les résultats" },
    filterData: {
      type: Object,
      default: () => ({ startDate: "", endDate: "" }),
    },
    /** When true, prepends a sequential row number column (per page). */
    showRowIndex: { type: Boolean, default: false },
  },
  data() {
    return {
            internalFilter: this.filterData ? { ...this.filterData } : { startDate: "", endDate: "" },
      internalSearchTerm: this.searchQuery || "",
      internalCurrentPage: this.page || 1,
      internalItemsPerPage: this.itemsPerPage,
      sortField: null,
      sortDirection: "asc",
      selectedRows: [],
    };
  },
  created() {
    // Initialize from URL parameters if syncWithUrl is enabled
    if (this.syncWithUrl) {
      this.initFromUrlParams();
    }
  },
  computed: {
    filteredData() {
      return this.data.filter((row) => {
        return Object.values(row).some((value) => {
          return (
            value !== null &&
            value.toString().toLowerCase().includes(this.internalSearchTerm.toLowerCase())
          );
        });
      });
    },

    sortedData() {
      if (!this.sortField) return this.filteredData;
      return this.filteredData.sort((a, b) => {
        const aValue = this.isNumeric(a[this.sortField]) ? parseFloat(a[this.sortField]) : a[this.sortField];
        const bValue = this.isNumeric(b[this.sortField]) ? parseFloat(b[this.sortField]) : b[this.sortField];

        if (aValue < bValue) return this.sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return this.sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    },

    paginatedData() {
      const start = (this.internalCurrentPage - 1) * this.internalItemsPerPage;
      const end = start + this.internalItemsPerPage;
      return this.sortedData.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredData.length / this.internalItemsPerPage);
    },

    sums() {
      const sums = {};
      this.summableColumns.forEach(column => {
        sums[column.field] = this.filteredData.reduce((total, row) => {
          return total + (parseFloat(row[column.field]) || 0);
        }, 0);
      });
      return sums;
    },

    emptyRowColspan() {
      return this.headers.length + (this.showRowIndex ? 1 : 0) + 3;
    },
  },
  watch: {
    // Watch for external prop changes
    page(newVal) {
      if (newVal !== null && newVal !== this.internalCurrentPage) {
        this.internalCurrentPage = newVal;
      }
    },
    searchQuery(newVal) {
      if (newVal !== null && newVal !== this.internalSearchTerm) {
        this.internalSearchTerm = newVal;
      }
    },
    itemsPerPage(newVal) {
      if (newVal !== this.internalItemsPerPage) {
        this.internalItemsPerPage = newVal;
      }
    },
    filterData: {
      handler(val) {
        if (!val || typeof val !== "object") return;
        this.internalFilter = { ...val };
      },
      deep: true,
    },
  },
  methods: {
    onFilter(filterValues) {
      // 🔥 Emit up to parent page
      this.$emit("filter", filterValues);
    },
    onReset(resetValues) {
      this.$emit("reset", resetValues);
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
    },

    prevPage() {
      this.internalCurrentPage--;
      this.emitPageChange();
    },

    nextPage() {
      this.internalCurrentPage++;
      this.emitPageChange();
    },

    emitPageChange() {
      this.$emit('update:page', this.internalCurrentPage);
      this.$emit('page-change', this.internalCurrentPage);
      this.updateUrlQueryParams();
    },

    updateUrlQueryParams() {
      // Only update URL if syncWithUrl is enabled
      if (this.syncWithUrl && typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('page', this.internalCurrentPage);

        // Also add search term if available
        if (this.internalSearchTerm) {
          url.searchParams.set('search', this.internalSearchTerm);
        } else {
          url.searchParams.delete('search');
        }

        // Also add items per page if different from default
        if (this.internalItemsPerPage !== 10) {
          url.searchParams.set('perPage', this.internalItemsPerPage);
        } else {
          url.searchParams.delete('perPage');
        }

        // Update URL without refreshing the page
        window.history.pushState({}, '', url);
      }
    },

    onSearchChange() {
      // Reset to page 1 when search changes
      this.internalCurrentPage = 1;
      this.$emit('update:searchQuery', this.internalSearchTerm);
      this.$emit('search-change', this.internalSearchTerm);
      this.emitPageChange();
    },

    onItemsPerPageChange() {
      // Reset to page 1 when items per page changes
      this.internalCurrentPage = 1;
      this.$emit('update:itemsPerPage', this.internalItemsPerPage);
      this.$emit('items-per-page-change', this.internalItemsPerPage);
      this.emitPageChange();
    },

    // Initialize from URL parameters on component mount
    initFromUrlParams() {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);

        // Get page from URL if exists
        const pageParam = urlParams.get('page');
        if (pageParam && !isNaN(parseInt(pageParam))) {
          this.internalCurrentPage = parseInt(pageParam);
        }

        // Get search from URL if exists
        const searchParam = urlParams.get('search');
        if (searchParam) {
          this.internalSearchTerm = searchParam;
        }

        // Get perPage from URL if exists
        const perPageParam = urlParams.get('perPage');
        if (perPageParam && !isNaN(parseInt(perPageParam))) {
          this.internalItemsPerPage = parseInt(perPageParam);
        }
      }
    },

    isSummableColumn(field) {
      return this.summableColumns.some(column => column.field === field);
    },

    getFooterClass(field) {
      const summableColumn = this.summableColumns.find((column) => column.field === field);
      return summableColumn ? summableColumn.class : '';
    },

    isNumeric(value) {
      return !isNaN(value) && !isNaN(parseFloat(value));
    },

    formatCellValue(value, column) {
      if (column.isAmount) {
        return this.formatNumber(value);
      }
      return value;
    },

    getFormattedSum(field) {
      const sum = this.getSum(field);
      return this.formatNumber(sum);
    },

    formatNumber(number) {
      if (number === null || number === undefined) return '0';

      const formattedNumber = new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(number);

      return formattedNumber.replace(/\s/g, ' ');
    },

    getSum(field) {
      return this.filteredData.reduce((total, row) => {
        return total + (parseFloat(row[field]) || 0);
      }, 0);
    },

    filteredButtons(row) {
      return this.buttons.filter(button => {
        return !button.condition || button.condition(row);
      });
    },

    rowDisplayIndex(index) {
      return (this.internalCurrentPage - 1) * this.internalItemsPerPage + index + 1;
    },
  }
};
</script>

<style scoped>
.data-table {
  width: 100%;
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-filter {
  display: flex;
  align-items: center;
}

.search-filter input {
  margin-right: 0.5rem;
}

.pagination {
  display: flex;
  align-items: center;
}

.clickable {
  cursor: pointer;
}

.sort-icon {
  display: inline-block;
  margin-left: 0.5rem;
}

.sort-icon.asc:before {
  font-weight: normal !important;
  background: #c4c4c4;
}

.sort-icon.desc:before {
  font-weight: bolder !important;
  background: #ffbaba;
}

.btn i {
  margin-right: 0.3rem;
}
</style>